import type { Key } from 'react';
import type { UseFormSetValue } from 'react-hook-form';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import type { Metric } from '@gonfalon/metrics/types';
import {
  Button,
  ListBox,
  ListBoxItem,
  Popover,
  Radio,
  RadioGroup,
  Select,
  SelectValue,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import type { MetricForm } from '../../../CreateMetricModal/formUtils';

/** Both variants write the same shape, so neither can drift from the other. */
const setEventDefault = (setValue: UseFormSetValue<MetricForm>, disabled: boolean) =>
  setValue('eventDefault', disabled ? { disabled: true } : { disabled: false, value: 0 }, { shouldDirty: true });

interface EventDefaultPickerProps {
  unitsWithoutEvents: 'included' | 'excluded';
  setValue: UseFormSetValue<MetricForm>;
  className?: string;
  metricKind: Metric['kind'];
}

/**
 * Collapsed variant for the sectioned layout, where this sits in a labelled row alongside
 * the other single-line choices. Wording matches the metric detail page.
 */
export const EventDefaultSelect: React.FC<
  Omit<EventDefaultPickerProps, 'metricKind'> & {
    /** Set when a row's visible label names this picker, replacing its own `aria-label`. */
    ariaLabelledBy?: string;
  }
> = ({ unitsWithoutEvents, setValue, className, ariaLabelledBy }) => {
  function handleChange(key: Key | null) {
    if (key === 'excluded' || key === 'included') {
      setEventDefault(setValue, key === 'excluded');
    }
  }

  return (
    <Select
      className={className}
      onChange={handleChange}
      value={unitsWithoutEvents}
      aria-label={ariaLabelledBy ? undefined : msg('metrics.metricDefinition.unitsWithoutEventsPickerLabel')}
      aria-labelledby={ariaLabelledBy}
    >
      <Button data-test-id="event-default-select">
        <SelectValue />
        <Icon name="chevron-down" size="small" />
      </Button>
      <Popover>
        <ListBox>
          <ListBoxItem id="included">{msg('metrics.metricDefinition.includedWithZero')}</ListBoxItem>
          <ListBoxItem id="excluded">{msg('metrics.metricDefinition.excluded')}</ListBoxItem>
        </ListBox>
      </Popover>
    </Select>
  );
};

export const EventDefaultPicker: React.FC<EventDefaultPickerProps> = (props: EventDefaultPickerProps) => {
  function onEventDefaultChange(value: string) {
    setEventDefault(props.setValue, value === 'excluded');
  }
  return (
    <RadioGroup
      className={props.className}
      onChange={onEventDefaultChange}
      value={props.unitsWithoutEvents}
      id="eventDefault"
      aria-label={msg('metrics.metricDefinition.unitsWithoutEventsPickerLabel')}
    >
      <Radio value="included">
        <Message id="metrics.metricDefinition.includeUnits" />
      </Radio>
      <Radio value="excluded">
        <Message id="metrics.metricDefinition.excludeUnits" values={{ kind: props.metricKind ?? 'custom' }} />
      </Radio>
    </RadioGroup>
  );
};
